import type { HttpTransport, TransportResponse } from '@mangovoice/api-client'

import type { PaginatedApiResult, UserListParams } from '../types/api.types'
import type {
  MyProfile,
  PbxUser,
  PbxUserCreate,
  PbxUserUpdate,
  UpdateMyProfilePayload,
  UserAssignedRole,
  UserAssignedRolesResponse,
  UserAssociatedPbx,
  UserCreate,
  UserDetail,
  UserListItem,
  UserRole,
  UserUpdate,
} from '../types/user.types'
import { USER_ENDPOINTS } from './endpoints'

export type UserApiClient = Pick<
  HttpTransport,
  'get' | 'post' | 'patch' | 'delete'
>

type GlobalUserListParams = UserListParams & { pbx_id?: number }

type GetUsersFn = {
  (
    params?: GlobalUserListParams,
  ): Promise<TransportResponse<PaginatedApiResult<UserListItem>>>
  (
    pbxId: number,
    params?: UserListParams,
  ): Promise<TransportResponse<PaginatedApiResult<PbxUser>>>
}

/**
 * Create user API functions from a client
 */
export const createUserApi = (client: UserApiClient) => {
  const getUsers = ((
    pbxIdOrParams?: number | GlobalUserListParams,
    params: UserListParams = {},
  ) => {
    if (typeof pbxIdOrParams === 'number') {
      return client.get<PaginatedApiResult<PbxUser>>(
        USER_ENDPOINTS.PBX_LIST(pbxIdOrParams),
        params as Record<string, unknown>,
      )
    }

    return client.get<PaginatedApiResult<UserListItem>>(
      USER_ENDPOINTS.GLOBAL_LIST(),
      (pbxIdOrParams ?? {}) as Record<string, unknown>,
    )
  }) as GetUsersFn

  return {
    // Global user endpoints
    getUsers,

    getUser: (userId: number) =>
      client.get<UserDetail>(USER_ENDPOINTS.DETAIL(userId)),

    getUserAssignedRoles: (userId: number) =>
      client.get<
        | UserAssignedRolesResponse
        | UserAssignedRole[]
        | { roles?: UserAssignedRole[] }
      >(USER_ENDPOINTS.USER_ROLES(userId)),

    getUserPbxes: (userId: number) =>
      client.get<
        | UserAssociatedPbx[]
        | PaginatedApiResult<UserAssociatedPbx>
        | { pbxes?: UserAssociatedPbx[]; results?: UserAssociatedPbx[] }
      >(USER_ENDPOINTS.USER_PBXES(userId)),

    createUser: (data: UserCreate) =>
      client.post<UserDetail>(USER_ENDPOINTS.CREATE(), data),

    updateUser: (userId: number, data: UserUpdate) =>
      client.patch<UserDetail>(USER_ENDPOINTS.UPDATE(userId), data),

    getUserRoles: () =>
      client.get<
        | UserRole[]
        | PaginatedApiResult<UserRole>
        | { roles?: UserRole[]; results?: UserRole[] }
      >(USER_ENDPOINTS.ROLES()),

    // PBX-scoped user endpoints
    getPbxUsers: (pbxId: number, params?: UserListParams) =>
      client.get<PaginatedApiResult<PbxUser>>(
        USER_ENDPOINTS.PBX_LIST(pbxId),
        params as Record<string, unknown>,
      ),

    addUserToPbx: (pbxId: number, data: PbxUserCreate) =>
      client.post<PbxUser>(USER_ENDPOINTS.ADD_TO_PBX(pbxId), data),

    updatePbxUser: (pbxId: number, userId: number, data: PbxUserUpdate) =>
      client.patch<PbxUser>(
        USER_ENDPOINTS.UPDATE_PBX_USER(pbxId, userId),
        data,
      ),

    removeUserFromPbx: (
      pbxId: number,
      userId: number,
      reassignAssetsTo?: number,
    ) => {
      const base = USER_ENDPOINTS.REMOVE_FROM_PBX(pbxId, userId)
      const url =
        reassignAssetsTo != null
          ? `${base}?reassign_assets_to=${reassignAssetsTo}`
          : base
      return client.delete<void>(url)
    },

    getMyProfile: (userId: number) =>
      client.get<MyProfile>(USER_ENDPOINTS.MY_PROFILE(userId)),

    updateMyProfile: (userId: number, payload: UpdateMyProfilePayload) =>
      client.patch<void>(USER_ENDPOINTS.UPDATE_MY_PROFILE(userId), {
        first_name: payload.first_name,
        last_name: payload.last_name,
      }),

    resetPassword: (userId: number) =>
      client.get<{ reset_url: string }>(USER_ENDPOINTS.RESET_PASSWORD(userId)),
  }
}

export type UserApi = ReturnType<typeof createUserApi>
